/**
 * Route Protection Configuration
 *
 * Centralized configuration for route protection and authentication
 * This ensures consistent security across deep linking, navigation, and direct URL access
 */

export interface RouteConfig {
  path: string;
  requiresAuth: boolean;
  requiredPermissions?: string[];
  category: 'public' | 'protected';
  description: string;
}

/**
 * Complete list of all routes in the application with their protection status
 */
export const ROUTE_PROTECTION_CONFIG: Record<string, RouteConfig> = {
  // ============================================
  // PUBLIC ROUTES (No Authentication Required)
  // ============================================

  // Auth Routes
  LOGIN: {
    path: '/auth/login',
    requiresAuth: false,
    category: 'public',
    description: 'User login'
  },
  SIGNUP: {
    path: '/auth/signup',
    requiresAuth: false,
    category: 'public',
    description: 'User registration'
  },
  SETUP: {
    path: '/auth/setup',
    requiresAuth: false,
    category: 'public',
    description: 'Pre-auth setup/redirect handler'
  },
  HOME_STATIC: {
    path: '/auth/home-static',
    requiresAuth: false,
    category: 'public',
    description: 'Public home page'
  },
  CHOOSE_GAMES: {
    path: '/auth/choose-games',
    requiresAuth: false,
    category: 'public',
    description: 'Game selection'
  },
  CHOOSE_AVATAR: {
    path: '/auth/choose-avatar',
    requiresAuth: false,
    category: 'public',
    description: 'Avatar selection'
  },
  EMAIL_CONFIRMATION: {
    path: '/auth/email-confirmation',
    requiresAuth: false,
    category: 'public',
    description: 'Email verification'
  },
  RESET_PASSWORD: {
    path: '/auth/reset-password',
    requiresAuth: false,
    category: 'public',
    description: 'Password reset request'
  },
  RESET_PASSWORD_CONFIRM: {
    path: '/auth/reset-password-confirm',
    requiresAuth: false,
    category: 'public',
    description: 'Password reset confirmation'
  },
  GAME_SHOW_PREVIEW: {
    path: '/events/game-show-preview',
    requiresAuth: false,
    category: 'public',
    description: 'Public game show preview'
  },
  EVENT_CREATE_TYPE: {
    path: '/events/create',
    requiresAuth: false, // NOTE: Review if this should be protected
    category: 'public',
    description: 'Event type selection'
  },

  // Payment Gateway Callback Routes (must be public for redirects)
  PAYMENT_SUCCESS_NEW: {
    path: '/payment/success',
    requiresAuth: false,
    category: 'public',
    description: 'Payment success callback'
  },
  PAYMENT_FAILED_NEW: {
    path: '/payment/failure',  // Changed to match Viva's redirect URL
    requiresAuth: false,
    category: 'public',
    description: 'Payment failed callback'
  },
  PAYMENT_CANCEL_NEW: {
    path: '/payment/cancel',
    requiresAuth: false,
    category: 'public',
    description: 'Payment cancelled callback'
  },

  // ============================================
  // PROTECTED ROUTES (Authentication Required)
  // ============================================

  // Dashboard & Home
  DASHBOARD: {
    path: '/dashboard',
    requiresAuth: true,
    category: 'protected',
    description: 'Main dashboard'
  },

  // Search Routes
  SEARCH_EVENT: {
    path: '/search',
    requiresAuth: true,
    category: 'protected',
    description: 'Search events'
  },
  SEARCH_GAMES: {
    path: '/search-games',
    requiresAuth: true,
    category: 'protected',
    description: 'Search games'
  },
  SEARCH_MOBILE: {
    path: '/search-mobile',
    requiresAuth: true,
    category: 'protected',
    description: 'Mobile search'
  },

  // Ticket Routes
  TICKET_DETAIL: {
    path: '/ticket-detail',
    requiresAuth: true,
    category: 'protected',
    description: 'Ticket details'
  },
  TICKET_HISTORY: {
    path: '/ticket-history',
    requiresAuth: true,
    category: 'protected',
    description: 'Ticket history'
  },
  REFUND_REQUEST: {
    path: '/refund-request',
    requiresAuth: true,
    category: 'protected',
    description: 'Refund requests'
  },

  // Event Routes
  EVENTS: {
    path: '/events',
    requiresAuth: true,
    category: 'protected',
    description: 'User events list'
  },
  GAME_SHOW: {
    path: '/events/game-show',
    requiresAuth: true,
    category: 'protected',
    description: 'Game show viewer'
  },
  ONGOING_EVENTS: {
    path: '/events/ongoing',
    requiresAuth: true,
    category: 'protected',
    description: 'Ongoing events'
  },
  PAST_EVENTS: {
    path: '/events/past',
    requiresAuth: true,
    category: 'protected',
    description: 'Past events'
  },
  CREATE_PUBLIC_EVENT: {
    path: '/events/create/public',
    requiresAuth: true,
    category: 'protected',
    description: 'Create public event'
  },
  CREATE_PRIVATE_EVENT: {
    path: '/events/create/private',
    requiresAuth: true,
    category: 'protected',
    description: 'Create private event'
  },
  UPDATE_PUBLIC_EVENT: {
    path: '/events/edit/public',
    requiresAuth: true,
    category: 'protected',
    description: 'Edit public event'
  },
  UPDATE_PRIVATE_EVENT: {
    path: '/events/edit/private',
    requiresAuth: true,
    category: 'protected',
    description: 'Edit private event'
  },
  EVENT_SUMMARY: {
    path: '/event-summary',
    requiresAuth: true,
    category: 'protected',
    description: 'Event summary'
  },
  PRIVATE_EVENT_SUMMARY: {
    path: '/private-event-summary',
    requiresAuth: true,
    category: 'protected',
    description: 'Private event summary'
  },
  STREAM_SETUP: {
    path: '/events/stream-setup',
    requiresAuth: true,
    category: 'protected',
    description: 'Stream setup'
  },
  ONGOING_STREAMING_SETUP: {
    path: '/events/ongoing-streaming-setup',
    requiresAuth: true,
    category: 'protected',
    description: 'Ongoing stream setup'
  },
  GO_LIVE: {
    path: '/events/go-live',
    requiresAuth: true,
    category: 'protected',
    description: 'Go live screen'
  },
  LIVE_STREAMING: {
    path: '/events/live-stream',
    requiresAuth: true,
    category: 'protected',
    description: 'Live streaming'
  },

  // Chat Routes
  MESSAGES: {
    path: '/messages',
    requiresAuth: true,
    category: 'protected',
    description: 'Chat messages'
  },
  ADD_FRIENDS: {
    path: '/messages/add-friends',
    requiresAuth: true,
    category: 'protected',
    description: 'Add friends'
  },

  // Notification Routes
  NOTIFICATIONS: {
    path: '/notifications',
    requiresAuth: true,
    category: 'protected',
    description: 'User notifications'
  },

  // Profile Routes
  PROFILE: {
    path: '/profile',
    requiresAuth: true,
    category: 'protected',
    description: 'User profile'
  },
  CHANGE_AVATAR: {
    path: '/profile/change-avatar',
    requiresAuth: true,
    category: 'protected',
    description: 'Change avatar'
  },
  REDEEM: {
    path: '/profile/redeem',
    requiresAuth: true,
    category: 'protected',
    description: 'Redeem rewards'
  },
  TRANSACTIONS: {
    path: '/profile/transactions',
    requiresAuth: true,
    category: 'protected',
    description: 'Transaction history'
  },
  USER_PROFILE: {
    path: '/profile/user',
    requiresAuth: true,
    category: 'protected',
    description: 'Other user profile'
  },

  // Purchase Routes
  PURCHASE: {
    path: '/profile/purchase-tokens',
    requiresAuth: true,
    category: 'protected',
    description: 'Purchase tokens'
  },
  PAYMENT_SUCCESS: {
    path: '/payment-success',
    requiresAuth: true,
    category: 'protected',
    description: 'Payment success'
  },

  // Settings Routes
  SETTINGS: {
    path: '/settings',
    requiresAuth: true,
    category: 'protected',
    description: 'App settings'
  },

  // Help & Support Routes
  HELP: {
    path: '/help',
    requiresAuth: true,
    category: 'protected',
    description: 'Help center'
  },
  HELP_DISPUTES: {
    path: '/help/disputes',
    requiresAuth: true,
    category: 'protected',
    description: 'Dispute center'
  },
  HELP_CONTACT: {
    path: '/help/contact-us',
    requiresAuth: true,
    category: 'protected',
    description: 'Contact support'
  },
  HELP_CREATE_TICKET: {
    path: '/help/create-ticket',
    requiresAuth: true,
    category: 'protected',
    description: 'Create support ticket'
  },

  // Dispute Routes
  DISPUTES: {
    path: '/disputes',
    requiresAuth: true,
    category: 'protected',
    description: 'Dispute management'
  },
  DISPUTE_DETAIL: {
    path: '/disputes/detail',
    requiresAuth: true,
    category: 'protected',
    description: 'Dispute details'
  },
  DISPUTE_CENTER: {
    path: '/dispute-center',
    requiresAuth: true,
    category: 'protected',
    description: 'Dispute center'
  },
};

/**
 * Get all protected route paths
 */
export function getProtectedRoutePaths(): string[] {
  return Object.values(ROUTE_PROTECTION_CONFIG)
    .filter(route => route.requiresAuth)
    .map(route => route.path);
}

/**
 * Get all public route paths
 */
export function getPublicRoutePaths(): string[] {
  return Object.values(ROUTE_PROTECTION_CONFIG)
    .filter(route => !route.requiresAuth)
    .map(route => route.path);
}

/**
 * Check if a given path requires authentication
 *
 * @param path - The URL path to check
 * @returns true if path requires authentication
 */
export function isProtectedRoute(path: string): boolean {
  // Normalize path (remove query params and trailing slash)
  const normalizedPath = path.split('?')[0].replace(/\/$/, '');

  // Check exact match first
  const exactMatch = Object.values(ROUTE_PROTECTION_CONFIG).find(
    route => route.path === normalizedPath
  );

  if (exactMatch) {
    return exactMatch.requiresAuth;
  }

  // Check if path starts with any protected route (for dynamic segments)
  const protectedPaths = getProtectedRoutePaths();
  return protectedPaths.some(protectedPath =>
    normalizedPath.startsWith(protectedPath)
  );
}

/**
 * Get route configuration for a given path
 */
export function getRouteConfig(path: string): RouteConfig | null {
  const normalizedPath = path.split('?')[0].replace(/\/$/, '');

  return Object.values(ROUTE_PROTECTION_CONFIG).find(
    route => route.path === normalizedPath || normalizedPath.startsWith(route.path)
  ) || null;
}

/**
 * Validate if user has permission to access a route
 *
 * @param path - The URL path
 * @param isAuthenticated - Whether user is authenticated
 * @param userPermissions - User's permissions (for future RBAC)
 * @returns { allowed: boolean, reason?: string }
 */
export function validateRouteAccess(
  path: string,
  isAuthenticated: boolean,
  userPermissions: string[] = []
): { allowed: boolean; reason?: string; redirectTo?: string } {
  const routeConfig = getRouteConfig(path);

  if (!routeConfig) {
    // Route not found in config - allow by default but log warning
    console.warn(`[RouteProtection] Unknown route: ${path}`);
    return { allowed: true };
  }

  // Check authentication requirement
  if (routeConfig.requiresAuth && !isAuthenticated) {
    return {
      allowed: false,
      reason: 'Authentication required',
      redirectTo: `/auth/setup?path=${encodeURIComponent(path)}`
    };
  }

  // Check permissions (for future RBAC implementation)
  if (routeConfig.requiredPermissions && routeConfig.requiredPermissions.length > 0) {
    const hasPermission = routeConfig.requiredPermissions.every(
      permission => userPermissions.includes(permission)
    );

    if (!hasPermission) {
      return {
        allowed: false,
        reason: 'Insufficient permissions',
        redirectTo: '/unauthorized'
      };
    }
  }

  return { allowed: true };
}

/**
 * Route categories for easier management
 */
export const ROUTE_CATEGORIES = {
  AUTH: ['/auth/login', '/auth/signup', '/auth/setup', '/auth/home-static', '/auth/choose-games', '/auth/choose-avatar', '/auth/email-confirmation', '/auth/reset-password', '/auth/reset-password-confirm'],
  EVENTS: ['/events', '/events/game-show', '/events/ongoing', '/events/past', '/events/create/public', '/events/create/private', '/events/edit/public', '/events/edit/private'],
  PROFILE: ['/profile', '/profile/change-avatar', '/profile/redeem', '/profile/transactions', '/profile/user'],
  MESSAGING: ['/messages', '/messages/add-friends'],
  HELP: ['/help', '/help/disputes', '/help/contact-us', '/help/create-ticket'],
  DISPUTES: ['/disputes', '/disputes/detail', '/dispute-center'],
  SETTINGS: ['/settings'],
  NOTIFICATIONS: ['/notifications'],
  PURCHASE: ['/profile/purchase-tokens', '/payment-success'],
} as const;
